import { useState, useEffect } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "@/store";
import { store } from "@/store";
import { getListUser } from "@/store/user/user.action";
import { getListAgencies } from "@/store/constants/constants.action";
import { getCreditList } from "@/store/media/media.action";
import { MediaModel } from "@/database";
import Modal from "../Modal/Modal";
import { CustomSelect } from "..";
import { COUNTRIES } from "@/utils/constants/uiConstants";
import { formatEntityName } from "@/utils/utilFunctions/entityName";

/* ── Field definitions ── */
const METADATA_FIELDS = [
  { key: "title", label: "TITLE", wide: true },
  { key: "desc", label: "DESCRIPTION", wide: true },
  { key: "tags", label: "TAGS", wide: true },
  { key: "team", label: "TEAM", wide: false },
  { key: "category", label: "CATEGORY", wide: false },
  { key: "subcategory", label: "SUBCATEGORY", wide: false },
  { key: "lang", label: "LANGUAGE", wide: false },
  { key: "agency", label: "AGENCY", wide: false },
  { key: "production_type", label: "PRODUCTION TYPE", wide: false },
  // { key: "ctype", label: "CONTENT TYPE", wide: false },
  { key: "mfk", label: "CONTENT RATING", wide: false },
] as const;

const CONTRIBUTOR_FIELDS = [
  { key: "producer", label: "PRODUCED BY" },
  { key: "anchor", label: "ANCHOR" },
  { key: "script_writer", label: "SCRIPT WRITER" },
  { key: "editor", label: "EDITOR" },
  { key: "cameraman", label: "CAMERAMAN" },
  { key: "photographer", label: "PHOTOGRAPHER" },
  { key: "researcher", label: "RESEARCHER" },
  { key: "compiler", label: "COMPILER" },
  { key: "curator", label: "CURATOR" },
  { key: "reporter", label: "REPORTER" },
  { key: "author", label: "AUTHOR" },
] as const;

const CREDIT_FIELDS = [
  { key: "imagecredit", label: "IMAGE CREDIT", creditKey: "image" },
  { key: "videocredit", label: "VIDEO CREDIT", creditKey: "video" },
  { key: "audiocredit", label: "AUDIO CREDIT", creditKey: "audio" },
] as const;

const AGENCY_CREDIT_FIELD = {
  key: "agency_credits",
  label: "AGENCY CREDITS",
} as const;

const GEO_FIELDS = ["gct", "gcl"] as const;

const ALL_KEYS = [
  ...METADATA_FIELDS.map((f) => f.key),
  AGENCY_CREDIT_FIELD.key,
  ...CONTRIBUTOR_FIELDS.map((f) => f.key),
  ...CREDIT_FIELDS.map((f) => f.key),
  ...GEO_FIELDS,
];

const GCT_OPTIONS = [
  { label: "Allowed Countries", value: 1 },
  { label: "Blocked Countries", value: 2 },
];

const formatFieldValue = (value: any): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  return String(value);
};

export interface BorrowCreditMeta {
  teamCredit?: { label: string; value: string }[];
  agencyCredit?: { label: string; value: string }[];
  contributorCredit?: { label: string; value: string }[];
  /** Resolved label/value pairs for imagecredit/videocredit/audiocredit IDs */
  creditData?: { label: string; value: string }[];
  /** Resolved label/value pairs for agency_credits IDs */
  agencyCreditData?: { label: string; value: string }[];
}

interface BorrowMetadataModalProps {
  isOpen: boolean;
  mediaData: MediaModel;
  onApply: (synced: Record<string, any>, creditMeta: BorrowCreditMeta) => void;
  onClose: () => void;
}

const BorrowMetadataModal = ({
  isOpen,
  mediaData,
  onApply,
  onClose,
  userList,
  imageCreditList,
  videoCreditList,
  audioCreditList,
  getListUser,
  getListAgencies,
}: Props) => {
  const md = mediaData as any;

  const [selectedFields, setSelectedFields] = useState<Set<string>>(
    new Set(ALL_KEYS)
  );
  const [agencyOptions, setAgencyOptions] = useState<any[]>([]);

  /* Name resolution from enriched fields */
  const contributorCredit: { label: string; value: string }[] = Array.isArray(
    md.userContributorData
  )
    ? md.userContributorData
        .filter((u: any) => u.id && u.name)
        .map((u: any) => ({ label: u.name, value: u.id }))
    : [];

  const creditsData: { label: string; value: string }[] = Array.isArray(
    md.creditsData
  )
    ? md.creditsData
        .filter((u: any) => u.id && u.name)
        .map((u: any) => ({ label: u.name, value: u.id }))
    : [];

  const teamCredit = md.teamdata?.id
    ? [{ label: formatEntityName(md.teamdata), value: md.teamdata.id }]
    : undefined;

  const agencyCreditData: { label: string; value: string }[] = Array.isArray(
    md.agencyCreditData
  )
    ? md.agencyCreditData
        .filter((u: any) => u.id && u.name)
        .map((u: any) => ({
          label: `${formatEntityName(u)}${u.email ? ` (${u.email})` : ""}`,
          value: u.id,
        }))
    : [];

  /* Seed agency options on open */
  useEffect(() => {
    if (!isOpen) return;
    setSelectedFields(new Set(ALL_KEYS));
    if (md.agencydata?.id && md.agencydata?.name) {
      setAgencyOptions([
        {
          id: md.agencydata.id,
          name: md.agencydata.name,
          display_name: md.agencydata.display_name,
        },
      ]);
    } else if (md.agency) {
      getListAgencies?.({ name: "", setInRedux: false }).then((res: any) => {
        setAgencyOptions(res || []);
      });
    }
  }, [isOpen]);

  const toggleField = (key: string) => {
    setSelectedFields((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedFields(
      selectedFields.size === ALL_KEYS.length ? new Set() : new Set(ALL_KEYS)
    );
  };

  const handleApply = () => {
    const synced: Record<string, any> = {};
    selectedFields.forEach((key) => {
      const val = md[key];
      const empty =
        val === undefined ||
        val === null ||
        val === "" ||
        (Array.isArray(val) && val.length === 0);
      if (!empty) synced[key] = val;
    });

    const creditMeta: BorrowCreditMeta = {
      teamCredit: teamCredit,
      agencyCredit: md.agencydata?.id
        ? [{ label: formatEntityName(md.agencydata), value: md.agencydata.id }]
        : agencyOptions.map((a: any) => ({
            label: formatEntityName(a),
            value: a.id,
          })),
      contributorCredit,
      creditData: creditsData,
      agencyCreditData,
    };

    onApply(synced, creditMeta);
  };

  const hasAgencyCredits =
    Array.isArray(md.agency_credits) && md.agency_credits.length > 0;

  const hasContributors = CONTRIBUTOR_FIELDS.some((f) => {
    const v = md[f.key];
    return Array.isArray(v) && v.length > 0;
  });

  const hasCredits = CREDIT_FIELDS.some((f) => {
    const v = md[f.key];
    return Array.isArray(v) && v.length > 0;
  });

  const hasGeo = !!(
    (md.gct && md.gct !== 0) ||
    (Array.isArray(md.gcl) && md.gcl.length > 0)
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="BORROW METADATA"
      width="820px"
      height="auto"
      closeOnOutsideClick={false}
      footer={
        <div className="d-flex justify-content-between w-100">
          <button className="btn btn-outline-primary btn-sm" onClick={onClose}>
            Back
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleApply}
            disabled={selectedFields.size === 0}
          >
            Apply
          </button>
        </div>
      }
    >
      <div className="p-3" style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <span className="text-muted small">
            Select fields to borrow from source
          </span>
          <button
            className="btn btn-link btn-sm p-0 text-decoration-none"
            onClick={toggleAll}
          >
            {selectedFields.size === ALL_KEYS.length
              ? "Deselect All"
              : "Select All"}
          </button>
        </div>

        {/* ── Metadata fields ── */}
        <div className="row m-0 gy-3">
          {METADATA_FIELDS.map(({ key, label, wide }) => {
            const value = md[key];
            const hasValue =
              value !== undefined &&
              value !== null &&
              value !== "" &&
              !(Array.isArray(value) && value.length === 0);

            if (!hasValue) return null;

            if (key === "tags") {
              return (
                <div key={key} className="col-12">
                  <label className="d-flex align-items-center gap-2 mb-1">
                    <input
                      type="checkbox"
                      className="form-check-input m-0"
                      checked={selectedFields.has(key)}
                      onChange={() => toggleField(key)}
                    />
                    <span
                      className="fw-medium text-uppercase"
                      style={{ fontSize: "0.75rem" }}
                    >
                      {label}
                    </span>
                  </label>
                  <div className="d-flex flex-wrap gap-1">
                    {(value as string[]).map((tag, i) => (
                      <span
                        key={i}
                        className="badge bg-light text-dark border"
                        style={{ fontSize: "0.75rem", fontWeight: 400 }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              );
            }

            if (key === "title" || key === "desc") {
              return (
                <div key={key} className="col-12">
                  <label className="d-flex align-items-center gap-2 mb-1">
                    <input
                      type="checkbox"
                      className="form-check-input m-0"
                      checked={selectedFields.has(key)}
                      onChange={() => toggleField(key)}
                    />
                    <span
                      className="fw-medium text-uppercase"
                      style={{ fontSize: "0.75rem" }}
                    >
                      {label}
                    </span>
                  </label>
                  <div
                    className="text-muted"
                    style={{
                      fontSize: "0.85rem",
                      maxHeight: 48,
                      overflow: "hidden",
                    }}
                  >
                    {formatFieldValue(value)}
                  </div>
                </div>
              );
            }

            if (key === "team") {
              return (
                <div
                  key={key}
                  className={wide ? "col-12" : "col-sm-12 col-md-4"}
                >
                  <CustomSelect
                    label="TEAM/PROPERTY"
                    value={value}
                    options={[]}
                    updatedOptions={[]}
                    showRequired={false}
                    placeholder=""
                    handleChange={() => {}}
                    labelCheckbox={true}
                    labelCheckboxChecked={selectedFields.has(key)}
                    onLabelCheckboxChange={() => toggleField(key)}
                    disabled={true}
                    hideArrow={true}
                    creditData={teamCredit}
                  />
                </div>
              );
            }

            if (key === "agency") {
              return (
                <div key={key} className="col-sm-12 col-md-4">
                  <CustomSelect
                    label="AGENCY"
                    value={String(value)}
                    options={agencyOptions.map((a: any) => ({
                      label: a.name,
                      value: a.id,
                    }))}
                    updatedOptions={agencyOptions.map((a: any) => ({
                      label: a.name,
                      value: a.id,
                    }))}
                    showRequired={false}
                    placeholder=""
                    handleChange={() => {}}
                    onInputChangeCallback={(params: any) => {
                      if (params.keyword.length >= 3) {
                        getListAgencies?.({
                          name: params.keyword,
                          setInRedux: false,
                        }).then((res: any) => setAgencyOptions(res || []));
                      }
                    }}
                    labelCheckbox={true}
                    labelCheckboxChecked={selectedFields.has(key)}
                    onLabelCheckboxChange={() => toggleField(key)}
                    disabled={true}
                    hideArrow={true}
                    loader={true}
                    creditData={agencyOptions.map((a: any) => ({
                      label: a.name,
                      value: a.id,
                    }))}
                  />
                </div>
              );
            }

            if (key === "mfk") {
              const mfkLabels: Record<number, string> = {
                1: "Made for Kids",
                2: "Made for General Audience",
                3: "Made for 18+",
              };
              return (
                <div key={key} className="col-sm-12 col-md-4">
                  <label className="d-flex align-items-center gap-2 mb-1">
                    <input
                      type="checkbox"
                      className="form-check-input m-0"
                      checked={selectedFields.has(key)}
                      onChange={() => toggleField(key)}
                    />
                    <span
                      className="fw-medium text-uppercase"
                      style={{ fontSize: "0.75rem" }}
                    >
                      {label}
                    </span>
                  </label>
                  <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                    {mfkLabels[value as number] ?? formatFieldValue(value)}
                  </div>
                </div>
              );
            }

            /* category / subcategory / lang / production_type / ctype */
            return (
              <div key={key} className="col-sm-12 col-md-4">
                <label className="d-flex align-items-center gap-2 mb-1">
                  <input
                    type="checkbox"
                    className="form-check-input m-0"
                    checked={selectedFields.has(key)}
                    onChange={() => toggleField(key)}
                  />
                  <span
                    className="fw-medium text-uppercase"
                    style={{ fontSize: "0.75rem" }}
                  >
                    {label}
                  </span>
                </label>
                <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                  {formatFieldValue(value)}
                </div>
              </div>
            );
          })}

          {/* agency_credits — inline after agency */}
          {hasAgencyCredits && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label={AGENCY_CREDIT_FIELD.label}
                value={md.agency_credits}
                options={[]}
                updatedOptions={agencyCreditData.map((u) => ({
                  label: u.label,
                  value: u.value,
                }))}
                multiSelect={true}
                hideArrow={true}
                loader={true}
                showRequired={false}
                handleChange={() => {}}
                labelCheckbox={true}
                labelCheckboxChecked={selectedFields.has(
                  AGENCY_CREDIT_FIELD.key
                )}
                onLabelCheckboxChange={() =>
                  toggleField(AGENCY_CREDIT_FIELD.key)
                }
                disabled={true}
                creditData={agencyCreditData}
              />
            </div>
          )}
        </div>

        {/* ── Contributors ── */}
        {hasContributors && (
          <>
            <h6 className="mt-4 mb-3">Contributors</h6>
            <div className="row m-0 gy-3">
              {CONTRIBUTOR_FIELDS.map(({ key, label }) => {
                const value = md[key];
                const hasValue = Array.isArray(value) && value.length > 0;
                if (!hasValue) return null;

                const creditDataForField = contributorCredit;
                const optionsList = userList?.map((u: any) => ({
                  label: u.name,
                  value: u.id,
                }));

                return (
                  <div key={key} className="col-sm-12 col-md-4">
                    <CustomSelect
                      label={label}
                      value={value}
                      options={[]}
                      updatedOptions={optionsList}
                      multiSelect={true}
                      hideArrow={true}
                      loader={true}
                      showRequired={false}
                      handleChange={() => {}}
                      onInputChangeCallback={(params: any) => {
                        if (params.keyword.length >= 3) {
                          getListUser?.({ name: params.keyword });
                        } else {
                          getListUser?.({ name: "" });
                        }
                      }}
                      labelCheckbox={true}
                      labelCheckboxChecked={selectedFields.has(key)}
                      onLabelCheckboxChange={() => toggleField(key)}
                      disabled={true}
                      creditData={creditDataForField}
                    />
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ── Credits ── */}
        {hasCredits && (
          <>
            <h6 className="mt-4 mb-3">Credits</h6>
            <div className="row m-0 gy-3">
              {CREDIT_FIELDS.map(({ key, label, creditKey }) => {
                const value = md[key];
                const hasValue = Array.isArray(value) && value.length > 0;
                if (!hasValue) return null;

                const optionsList =
                  creditKey === "image"
                    ? imageCreditList?.list?.map((u: any) => ({
                        label: u.name,
                        value: u.id,
                      }))
                    : creditKey === "video"
                      ? videoCreditList?.list?.map((u: any) => ({
                          label: u.name,
                          value: u.id,
                        }))
                      : audioCreditList?.list?.map((u: any) => ({
                          label: u.name,
                          value: u.id,
                        }));

                return (
                  <div key={key} className="col-sm-12 col-md-4">
                    <CustomSelect
                      label={label}
                      value={value}
                      options={[]}
                      updatedOptions={optionsList}
                      multiSelect={true}
                      hideArrow={true}
                      loader={true}
                      showRequired={false}
                      handleChange={() => {}}
                      onInputChangeCallback={(params: any) => {
                        if (params.keyword.length >= 3) {
                          store.dispatch(
                            getCreditList({
                              name: params.keyword,
                              key: creditKey,
                            })
                          );
                        } else {
                          store.dispatch(
                            getCreditList({ name: "", key: creditKey })
                          );
                        }
                      }}
                      labelCheckbox={true}
                      labelCheckboxChecked={selectedFields.has(key)}
                      onLabelCheckboxChange={() => toggleField(key)}
                      disabled={true}
                      creditData={creditsData}
                    />
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ── Geo Restriction ── */}
        {hasGeo && (
          <>
            <h6 className="mt-4 mb-3">Geo Restriction</h6>
            <div className="row m-0 gy-3">
              {md.gct && md.gct !== 0 && (
                <div className="col-sm-12 col-md-4">
                  <CustomSelect
                    label="MODE"
                    value={md.gct}
                    options={GCT_OPTIONS}
                    updatedOptions={GCT_OPTIONS}
                    showRequired={false}
                    placeholder="Mode"
                    handleChange={() => {}}
                    labelCheckbox={true}
                    labelCheckboxChecked={selectedFields.has("gct")}
                    onLabelCheckboxChange={() => toggleField("gct")}
                    disabled={true}
                    hideArrow={true}
                  />
                </div>
              )}
              {Array.isArray(md.gcl) && md.gcl.length > 0 && (
                <div className="col-sm-12 col-md-8">
                  <CustomSelect
                    label="COUNTRIES"
                    value={md.gcl}
                    options={COUNTRIES}
                    updatedOptions={COUNTRIES}
                    showRequired={false}
                    placeholder="Countries"
                    handleChange={() => {}}
                    multiSelect={true}
                    labelCheckbox={true}
                    labelCheckboxChecked={selectedFields.has("gcl")}
                    onLabelCheckboxChange={() => toggleField("gcl")}
                    disabled={true}
                    hideArrow={true}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

const mapState = (state: RootState) => ({
  userList: state.users.userList.list,
  imageCreditList: state.media.imageCreditList,
  videoCreditList: state.media.videoCreditList,
  audioCreditList: state.media.audioCreditList,
});

const mapDispatch = {
  getListUser,
  getListAgencies,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector> & BorrowMetadataModalProps;
export default connector(BorrowMetadataModal);
